import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import AreaShell from '../../chrome/AreaShell';
import { Checkbox, ConfirmDialog, Dialog, Field, Select, Textarea, TextInput } from '../../components/forms';
import { Segmented, TCard, TEmpty, TFoot, Toolbar, TRow, TSkeleton } from '../../components/table';
import { useToast } from '../../components/roster';
import useTemplates, { Template, TemplateType } from '@/src/Templates/hooks/useTemplates';
import useOrgs from '@/src/Orgs/components/hooks/useOrgs';
import useLicenseTypeManagement from '@/src/Licenses/components/hooks/useLicenseTypeManagement';
import './admin-content.css';

/*
 * PE templates: experience / processing-flow definitions with org and
 * license-type access. The JSON is parse-checked before save. Access
 * changes are drafted, then confirmed and sent one request per change.
 * useTemplates swallows errors (null / false / []), so results are
 * checked by value rather than by rejection.
 */

const EMPTY_DRAFT = {
    name: '',
    description: '',
    videoUrl: '',
    definitionJsonString: '',
    templateType: TemplateType.Experience,
};

type TypeFilter = 'all' | 'exp' | 'proc';
type Named = { id: number; name?: string };

const typeLabel = (t: TemplateType) => (t === TemplateType.Processing ? 'Processing flow' : 'Experience');
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function jsonProblem(text: string): string | null {
    if (!text.trim()) return 'Definition is required.';
    try {
        JSON.parse(text);
        return null;
    } catch (e: any) {
        return `Not valid JSON: ${e?.message ?? 'parse error'}`;
    }
}

const toggleIn = (set: Set<number>, id: number) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
};

export default function AdminTemplatesV3() {
    const {
        listTemplates,
        createTemplate,
        updateTemplate,
        deleteTemplate,
        assignToOrg,
        removeFromOrg,
        assignToLicenseType,
        removeFromLicenseType,
        getOrgAssignments,
        getLicenseTypeAssignments,
        error: hookError,
    } = useTemplates();
    const { listOrgs } = useOrgs();
    const { fetchLicenseTypes } = useLicenseTypeManagement();
    const toast = useToast();

    const [rows, setRows] = useState<Template[] | null>(null);
    const [query, setQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

    const load = useCallback(async () => {
        const data = (await listTemplates()) ?? [];
        setRows([...data].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        load();
    }, [load]);
    /* listTemplates returns [] on failure; the hook's error tells them apart */
    const listFailed = hookError === 'Failed to list templates';

    /* ---- create / edit ---- */
    const [dialog, setDialog] = useState<{ kind: 'create' } | { kind: 'edit'; row: Template } | null>(null);
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [tried, setTried] = useState(false);
    const savingRef = useRef(false);

    const openCreate = () => {
        setDraft(EMPTY_DRAFT);
        setSaveError('');
        setTried(false);
        setDialog({ kind: 'create' });
    };

    const openEdit = (row: Template) => {
        let pretty = row.definitionJsonString ?? '';
        try {
            pretty = JSON.stringify(JSON.parse(pretty), null, 2);
        } catch {
            /* keep raw; the save check flags it */
        }
        setDraft({
            name: row.name ?? '',
            description: row.description ?? '',
            videoUrl: row.videoUrl ?? '',
            definitionJsonString: pretty,
            templateType: row.templateType ?? TemplateType.Experience,
        });
        setSaveError('');
        setTried(false);
        setDialog({ kind: 'edit', row });
    };

    const jsonError = jsonProblem(draft.definitionJsonString);
    const nameError = draft.name.trim() ? null : 'Name is required.';

    const formatJson = () => {
        if (jsonError) return;
        setDraft((d) => ({ ...d, definitionJsonString: JSON.stringify(JSON.parse(d.definitionJsonString), null, 2) }));
    };

    const submit = async () => {
        if (!dialog || savingRef.current) return;
        setTried(true);
        if (nameError || jsonError) return;
        savingRef.current = true;
        setSaving(true);
        setSaveError('');
        const body = {
            name: draft.name.trim(),
            description: draft.description.trim(),
            videoUrl: draft.videoUrl.trim() || undefined,
            definitionJsonString: draft.definitionJsonString,
            templateType: draft.templateType,
        };
        const result =
            dialog.kind === 'create' ? await createTemplate(body) : await updateTemplate(dialog.row.id, body);
        savingRef.current = false;
        setSaving(false);
        if (result) {
            setDialog(null);
            toast.show(dialog.kind === 'create' ? `Created ${body.name}.` : `Saved ${body.name}.`);
            load();
        } else {
            setSaveError('Couldn’t save. Check the fields and try again.');
        }
    };

    /* ---- delete ---- */
    const [deleting, setDeleting] = useState<Template | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);
    const remove = async (row: Template) => {
        if (busyId !== null) return;
        setBusyId(row.id);
        const result = await deleteTemplate(row.id);
        setBusyId(null);
        if (result !== null) {
            setRows((cur) => cur && cur.filter((r) => r.id !== row.id));
            toast.show(`Deleted ${row.name}.`);
        } else {
            toast.show(`Couldn’t delete ${row.name}.`);
        }
    };

    /* ---- access: draft, confirm, then one request per change ---- */
    const [accessFor, setAccessFor] = useState<Template | null>(null);
    const [orgs, setOrgs] = useState<Named[] | null>(null);
    const [licenseTypes, setLicenseTypes] = useState<Named[] | null>(null);
    const [refsFailed, setRefsFailed] = useState(false);
    const [savedOrgs, setSavedOrgs] = useState<Set<number> | null>(null);
    const [savedTypes, setSavedTypes] = useState<Set<number> | null>(null);
    const [draftOrgs, setDraftOrgs] = useState<Set<number>>(new Set());
    const [draftTypes, setDraftTypes] = useState<Set<number>>(new Set());
    const [orgQuery, setOrgQuery] = useState('');
    const [confirmAccess, setConfirmAccess] = useState(false);
    const [accessSaving, setAccessSaving] = useState(false);
    const [accessError, setAccessError] = useState('');
    const accessFor$ = useRef<number | null>(null);

    const loadAccess = async (row: Template) => {
        accessFor$.current = row.id;
        setSavedOrgs(null);
        setSavedTypes(null);
        setRefsFailed(false);
        const [orgRows, typeRows, orgIds, typeIds] = await Promise.all([
            orgs ? Promise.resolve(orgs) : listOrgs().catch(() => null),
            licenseTypes ? Promise.resolve(licenseTypes) : fetchLicenseTypes().catch(() => null),
            getOrgAssignments(row.id),
            getLicenseTypeAssignments(row.id),
        ]);
        if (accessFor$.current !== row.id) return;
        if (!orgRows || !typeRows) {
            setRefsFailed(true);
            return;
        }
        setOrgs([...orgRows].sort((a: Named, b: Named) => (a.name ?? '').localeCompare(b.name ?? '')));
        setLicenseTypes(typeRows as Named[]);
        const o = new Set<number>(orgIds ?? []);
        const t = new Set<number>(typeIds ?? []);
        setSavedOrgs(o);
        setSavedTypes(t);
        setDraftOrgs(new Set(o));
        setDraftTypes(new Set(t));
    };

    const openAccess = (row: Template) => {
        setAccessError('');
        setOrgQuery('');
        setAccessFor(row);
        loadAccess(row);
    };
    const closeAccess = () => {
        if (accessSaving) return;
        accessFor$.current = null;
        setAccessFor(null);
    };

    const diff = useMemo(() => {
        const d = (saved: Set<number> | null, next: Set<number>) => ({
            add: saved ? Array.from(next).filter((id) => !saved.has(id)) : [],
            remove: saved ? Array.from(saved).filter((id) => !next.has(id)) : [],
        });
        return { orgs: d(savedOrgs, draftOrgs), types: d(savedTypes, draftTypes) };
    }, [savedOrgs, draftOrgs, savedTypes, draftTypes]);
    const accessChanges =
        diff.orgs.add.length + diff.orgs.remove.length + diff.types.add.length + diff.types.remove.length;

    const nameOf = (list: Named[] | null, id: number) => list?.find((x) => x.id === id)?.name ?? `#${id}`;
    const describe = (list: Named[] | null, ids: number[], noun: string) =>
        ids.length <= 3 ? ids.map((id) => nameOf(list, id)).join(', ') : plural(ids.length, noun);

    const accessSummary = () => {
        const parts: string[] = [];
        if (diff.orgs.add.length) parts.push(`add ${describe(orgs, diff.orgs.add, 'org')}`);
        if (diff.orgs.remove.length) parts.push(`remove ${describe(orgs, diff.orgs.remove, 'org')}`);
        if (diff.types.add.length)
            parts.push(`add every org on ${describe(licenseTypes, diff.types.add, 'license type')}`);
        if (diff.types.remove.length)
            parts.push(`remove every org on ${describe(licenseTypes, diff.types.remove, 'license type')}`);
        return parts.join('; ');
    };

    const saveAccess = async () => {
        const row = accessFor;
        if (!row || accessSaving || accessChanges === 0) return;
        setAccessSaving(true);
        setAccessError('');
        let failed = 0;
        const run = async (call: Promise<boolean>) => {
            if (!(await call)) failed++;
        };
        for (const id of diff.orgs.add) await run(assignToOrg(row.id, id));
        for (const id of diff.orgs.remove) await run(removeFromOrg(row.id, id));
        for (const id of diff.types.add) await run(assignToLicenseType(row.id, id));
        for (const id of diff.types.remove) await run(removeFromLicenseType(row.id, id));
        const total = accessChanges;
        setAccessSaving(false);
        if (failed === 0) {
            toast.show(`Saved ${plural(total, 'access change')} for ${row.name}.`);
            accessFor$.current = null;
            setAccessFor(null);
        } else {
            setAccessError(`${plural(failed, 'change')} failed. The lists now show what actually saved.`);
            loadAccess(row);
        }
    };

    const q = query.trim().toLowerCase();
    const visible = (rows ?? []).filter(
        (r) =>
            (typeFilter === 'all' ||
                (typeFilter === 'exp' ? r.templateType !== TemplateType.Processing : r.templateType === TemplateType.Processing)) &&
            (!q || (r.name ?? '').toLowerCase().includes(q) || (r.description ?? '').toLowerCase().includes(q)),
    );
    const oq = orgQuery.trim().toLowerCase();
    const orgsVisible = (orgs ?? []).filter((o) => !oq || (o.name ?? '').toLowerCase().includes(oq));
    const accessLoading = accessFor !== null && !refsFailed && (savedOrgs === null || savedTypes === null);

    const checkList = (
        list: Named[],
        saved: Set<number> | null,
        draftSet: Set<number>,
        setDraftSet: React.Dispatch<React.SetStateAction<Set<number>>>,
        what: string,
    ) =>
        list.length === 0 ? (
            <p className="quiet-empty">No {what}.</p>
        ) : (
            list.map((x) => {
                const on = draftSet.has(x.id);
                const changed = !!saved && saved.has(x.id) !== on;
                return (
                    <Checkbox
                        key={x.id}
                        label={`${x.name ?? `#${x.id}`}${changed ? (on ? ' (adding)' : ' (removing)') : ''}`}
                        checked={on}
                        disabled={accessSaving}
                        onChange={() => setDraftSet((cur) => toggleIn(cur, x.id))}
                    />
                );
            })
        );

    return (
        <AreaShell
            area="admin"
            tab="templates"
            sub="Experience and processing-flow templates orgs can start from"
            actions={
                <button type="button" className="tb-new" onClick={openCreate}>
                    New template
                </button>
            }
        >
            <Toolbar
                searchValue={query}
                onSearch={setQuery}
                searchPlaceholder="Search templates"
                filterCount={0}
                inline={
                    <Segmented
                        label="Type"
                        options={[
                            { value: 'all', label: 'All' },
                            { value: 'exp', label: 'Experience' },
                            { value: 'proc', label: 'Processing' },
                        ]}
                        value={typeFilter}
                        onChange={setTypeFilter}
                    />
                }
            />
            {listFailed && rows !== null && rows.length === 0 ? (
                <TCard>
                    <TEmpty message="Couldn’t load templates." actionLabel="Retry" onAction={load} />
                </TCard>
            ) : rows === null ? (
                <TCard>
                    <TSkeleton rows={4} />
                </TCard>
            ) : visible.length === 0 ? (
                <TCard>
                    <TEmpty
                        message={rows.length === 0 ? 'No templates yet.' : 'No templates match.'}
                        actionLabel={rows.length === 0 ? 'New template' : 'Clear filters'}
                        onAction={
                            rows.length === 0
                                ? openCreate
                                : () => {
                                      setQuery('');
                                      setTypeFilter('all');
                                  }
                        }
                    />
                </TCard>
            ) : (
                <TCard>
                    {visible.map((row, i) => (
                        <TRow
                            key={row.id}
                            index={i}
                            name={row.name}
                            badge={`v${row.version}`}
                            meta={row.description || 'No description'}
                            metaDate={typeLabel(row.templateType)}
                            date={row.updatedAt ? moment(row.updatedAt).format('MMM D, YYYY') : '—'}
                            status={busyId === row.id ? 'Deleting…' : typeLabel(row.templateType)}
                            onOpen={() => openEdit(row)}
                            menuItems={[
                                { label: 'Edit', onClick: () => openEdit(row) },
                                { label: 'Access…', onClick: () => openAccess(row) },
                                { label: 'Delete…', onClick: () => setDeleting(row) },
                            ]}
                        />
                    ))}
                </TCard>
            )}
            {rows !== null && !(listFailed && rows.length === 0) && (
                <TFoot>
                    {q || typeFilter !== 'all'
                        ? `${visible.length} of ${rows.length} · filtered`
                        : plural(rows.length, 'template')}
                </TFoot>
            )}

            <Dialog
                open={dialog !== null}
                title={dialog?.kind === 'edit' ? `Edit ${dialog.row.name}` : 'New template'}
                onClose={() => !saving && setDialog(null)}
                onSubmit={submit}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={() => setDialog(null)} disabled={saving}>
                            Cancel
                        </button>
                        <button type="submit" className="tb-new" disabled={saving || (tried && (!!nameError || !!jsonError))}>
                            {saving ? 'Saving…' : dialog?.kind === 'create' ? 'Create template' : 'Save'}
                        </button>
                    </>
                }
            >
                <div className="adm-wide">
                    {dialog?.kind === 'edit' && (
                        <p className="adm-scope">
                            Saving changes this template for every org and license type it’s assigned to.
                        </p>
                    )}
                    <div className="two">
                        <Field label="Name" error={tried && nameError ? nameError : undefined}>
                            <TextInput
                                autoFocus
                                value={draft.name}
                                disabled={saving}
                                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                            />
                        </Field>
                        <Field label="Type">
                            <Select
                                value={draft.templateType}
                                disabled={saving}
                                onChange={(e) =>
                                    setDraft((d) => ({ ...d, templateType: +e.target.value as TemplateType }))
                                }
                            >
                                <option value={TemplateType.Experience}>Experience</option>
                                <option value={TemplateType.Processing}>Processing flow</option>
                            </Select>
                        </Field>
                    </div>
                    <Field label="Description">
                        <Textarea
                            rows={2}
                            value={draft.description}
                            disabled={saving}
                            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                        />
                    </Field>
                    <Field label="Video URL (optional)">
                        <TextInput
                            type="url"
                            inputMode="url"
                            className="input adm-mono"
                            value={draft.videoUrl}
                            disabled={saving}
                            onChange={(e) => setDraft((d) => ({ ...d, videoUrl: e.target.value }))}
                        />
                    </Field>
                    <Field
                        label="Definition JSON"
                        help={jsonError ? undefined : 'Valid JSON.'}
                        error={jsonError && (tried || draft.definitionJsonString.trim()) ? jsonError : undefined}
                    >
                        <Textarea
                            className="input textarea adm-json"
                            rows={14}
                            wrap="off"
                            value={draft.definitionJsonString}
                            disabled={saving}
                            aria-invalid={!!jsonError}
                            onChange={(e) => setDraft((d) => ({ ...d, definitionJsonString: e.target.value }))}
                        />
                    </Field>
                    <div className="adm-row-act">
                        <button type="button" className="tb-btn" onClick={formatJson} disabled={!!jsonError || saving}>
                            Format JSON
                        </button>
                    </div>
                    {saveError && (
                        <p className="field-e" role="alert" style={{ marginTop: 10 }}>
                            {saveError}
                        </p>
                    )}
                </div>
            </Dialog>

            <Dialog
                open={accessFor !== null && !confirmAccess}
                title={accessFor ? `Access · ${accessFor.name}` : ''}
                onClose={closeAccess}
                onSubmit={() => accessChanges > 0 && setConfirmAccess(true)}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={closeAccess} disabled={accessSaving}>
                            Cancel
                        </button>
                        <button type="submit" className="tb-new" disabled={accessSaving || accessLoading || accessChanges === 0}>
                            {accessSaving
                                ? 'Saving…'
                                : accessChanges === 0
                                  ? 'No changes'
                                  : `Review ${plural(accessChanges, 'change')}`}
                        </button>
                    </>
                }
            >
                <div className="adm-wide">
                    <p className="adm-scope">
                        Orgs see this template if they’re listed here or hold one of the checked license types.
                    </p>
                    {refsFailed ? (
                        <>
                            <p className="quiet-empty" style={{ marginBottom: 10 }}>
                                Couldn’t load orgs or license types.
                            </p>
                            <button type="button" className="tb-btn" onClick={() => accessFor && loadAccess(accessFor)}>
                                Retry
                            </button>
                        </>
                    ) : accessLoading ? (
                        <p className="quiet-empty">Loading access…</p>
                    ) : (
                        <div className="adm-cols">
                            <div>
                                <p className="s-lab" style={{ margin: '0 0 6px' }}>
                                    Organizations · {draftOrgs.size}
                                </p>
                                <TextInput
                                    aria-label="Filter organizations"
                                    placeholder="Filter organizations"
                                    value={orgQuery}
                                    onChange={(e) => setOrgQuery(e.target.value)}
                                    style={{ marginBottom: 6 }}
                                />
                                <div className="adm-list">
                                    {checkList(orgsVisible, savedOrgs, draftOrgs, setDraftOrgs, oq ? 'matching orgs' : 'orgs')}
                                </div>
                            </div>
                            <div>
                                <p className="s-lab" style={{ margin: '0 0 6px' }}>
                                    License types · {draftTypes.size}
                                </p>
                                <div className="adm-list">
                                    {checkList(licenseTypes ?? [], savedTypes, draftTypes, setDraftTypes, 'license types')}
                                </div>
                            </div>
                        </div>
                    )}
                    {accessError && (
                        <p className="field-e" role="alert" style={{ marginTop: 10 }}>
                            {accessError}
                        </p>
                    )}
                </div>
            </Dialog>

            <ConfirmDialog
                open={confirmAccess}
                title={`Save ${plural(accessChanges, 'access change')}?`}
                body={`For “${accessFor?.name ?? ''}”: ${accessSummary()}. Changes apply immediately.`}
                actionLabel="Save access"
                danger={diff.orgs.remove.length + diff.types.remove.length > 0}
                onConfirm={saveAccess}
                onClose={() => setConfirmAccess(false)}
            />

            <ConfirmDialog
                open={deleting !== null}
                title={deleting ? `Delete ${deleting.name}?` : ''}
                body="Every org and license type assigned to this template loses access immediately. This can’t be undone."
                actionLabel="Delete template"
                onConfirm={() => deleting && remove(deleting)}
                onClose={() => setDeleting(null)}
            />
            {toast.node}
        </AreaShell>
    );
}
